'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { MeetingType } from '@ps/features/bio/custom-button/common/types/MeetingType';
import type { MeetingDatesResponse } from '@ps/features/bio/custom-button/variants/calendar/types/MeetingDatesResponse';
import { proxyFetch } from '@ps/functions/utils/proxyFetch';
import { addDays } from 'date-fns/addDays';
import { format } from 'date-fns/format';
import { startOfDay } from 'date-fns/startOfDay';

// ─── Types ────────────────────────────────────────────────────────────────

// The visitor calendar VMs (slim, `@ps/api`-free) served by the
// `/api/bio/availability/*` proxies. Re-exported so the widget's components
// name these here, not the generated transport types.
export type { MeetingSlot } from '@ps/features/bio/custom-button/common/types/MeetingSlot';
export type MeetingTypeItem = MeetingType;

// ─── Hook ─────────────────────────────────────────────────────────────────

export function useAvailableDates(storeId: string | undefined) {
  const [dates, setDates] = useState<Date[]>([]);
  const [calendarAuthorized, setCalendarAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const hasMoreRef = useRef(true);

  const timezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'UTC';
    }
  }, []);

  const fetchDates = useCallback(
    async (
      start: Date,
    ): Promise<{ dates: Date[]; calendarAuthorized: boolean }> => {
      if (!storeId) return { dates: [], calendarAuthorized: false };
      try {
        const params = new URLSearchParams({
          sellerId: storeId,
          startDate: format(start, 'yyyy-MM-dd'),
          timezone,
          freeOnly: 'true',
        });
        const res = await proxyFetch<MeetingDatesResponse>(
          `/api/bio/availability/dates?${params.toString()}`,
        );
        return {
          dates: res.dates.map(date => startOfDay(new Date(date))),
          calendarAuthorized: res.calendarAuthorized,
        };
      } catch {
        return { dates: [], calendarAuthorized: false };
      }
    },
    [storeId, timezone],
  );

  const fetchMeetingTypes = useCallback(
    async (date: Date): Promise<MeetingTypeItem[]> => {
      if (!storeId) return [];
      try {
        const params = new URLSearchParams({
          sellerId: storeId,
          date: format(date, 'yyyy-MM-dd'),
          timezone,
          freeOnly: 'true',
        });
        return await proxyFetch<MeetingType[]>(
          `/api/bio/availability/types?${params.toString()}`,
        );
      } catch {
        return [];
      }
    },
    [storeId, timezone],
  );

  // Initial load
  useEffect(() => {
    if (!storeId) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    hasMoreRef.current = true;

    fetchDates(startOfDay(new Date())).then(result => {
      if (cancelled) return;
      setDates(result.dates);
      setCalendarAuthorized(result.calendarAuthorized);
      hasMoreRef.current = result.dates.length > 0;
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [storeId, fetchDates]);

  // Pagination
  const loadMoreDates = useCallback(async () => {
    if (isLoadingMore || !hasMoreRef.current || dates.length === 0) return;
    setIsLoadingMore(true);
    try {
      const lastDate = dates[dates.length - 1];
      const result = await fetchDates(addDays(lastDate, 1));
      if (result.dates.length === 0) {
        hasMoreRef.current = false;
      } else {
        setDates(prev => [...prev, ...result.dates]);
      }
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, dates, fetchDates]);

  // Re-fetch from scratch (used after availability changes).
  // Keeps existing dates visible while fetching to avoid UI flash.
  const refresh = useCallback(() => {
    hasMoreRef.current = true;
    void fetchDates(startOfDay(new Date())).then(result => {
      setDates(result.dates);
      setCalendarAuthorized(result.calendarAuthorized);
      hasMoreRef.current = result.dates.length > 0;
    });
  }, [fetchDates]);

  return {
    dates,
    calendarAuthorized,
    isLoading,
    isLoadingMore,
    loadMoreDates,
    fetchMeetingTypes,
    refresh,
    timezone,
  };
}
